import { setToken } from "@cw-elements/config";
import {
  AccessGuardian,
  AuthContainer,
  AuthProvider as AccountsAuthProvider,
  type AuthEnvironment,
  useAuth as useAccountsAuth,
} from "@cw-elements/auth-provider";
import { t } from "i18next";
import React, { type PropsWithChildren, useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { appURLs } from "../appRoutes/appUrls";
import { sensitiveEnv } from "../../config/sensitiveEnv";
import { AxiosInstance } from "../../shared/utils/axios";
import { formatTranslation } from "../../shared/utils/translationUtils";
import { getAccessToken } from "./accessToken";

const AUTH_ENVIRONMENT = (process.env.REACT_APP_CROPWISE_PLATFORM_ENV ??
  "staging") as AuthEnvironment;
const TypedAccessGuardian =
  AccessGuardian as unknown as React.ComponentType<PropsWithChildren>;

const AccessTokenSync = (): null => {
  const { user } = useAccountsAuth();

  useEffect(() => {
    try {
      setToken(getAccessToken());
    } catch {
      console.error("CW Elements token synchronization failed");
    }
  }, [user]);

  return null;
};

export const AuthProvider = ({ children }: PropsWithChildren): JSX.Element => (
  <AccountsAuthProvider
    environment={AUTH_ENVIRONMENT}
    clientId={sensitiveEnv.oauthClientId}
    redirectUri={`${window.location.origin}/authenticate`}
    axiosInstance={AxiosInstance}
  >
    <AccessTokenSync />
    <Routes>
      <Route path="/authenticate" element={<AuthContainer />} />
      <Route
        path="/"
        element={<Navigate to={appURLs.MY_CROPWISE} replace={true} />}
      />
      <Route
        path="*"
        element={<TypedAccessGuardian>{children}</TypedAccessGuardian>}
      />
    </Routes>
  </AccountsAuthProvider>
);

interface AuthHookValue {
  user: any;
  error: string | null;
  status: string;
  logout: () => void;
  logoutStatus: boolean;
  setLogoutStatus: React.Dispatch<React.SetStateAction<boolean>>;
  isPending: boolean;
  isSuccess: boolean;
  isError: boolean;
  isAuthenticated: boolean;
}

const useAuth = (): AuthHookValue => {
  const { user, hasSigninFailed, signout } = useAccountsAuth();
  const [logoutStatus, setLogoutStatus] = useState<boolean>(false);
  const status = hasSigninFailed ? "error" : user ? "success" : "loading";
  const logout = (): void => {
    localStorage.removeItem("tokens");
    sessionStorage.removeItem("tokens");
    try {
      setToken("");
    } catch {
      console.error("CW Elements token synchronization failed");
    }
    signout();
  };

  return {
    user: user as any,
    error: hasSigninFailed
      ? formatTranslation(t("auth.somethingWentWrong"))
      : null,
    status,
    logout,
    logoutStatus,
    setLogoutStatus,
    isPending: status === "loading",
    isSuccess: status === "success",
    isError: status === "error",
    isAuthenticated: Boolean(user),
  };
};

export { useAuth };
